import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { BulkActions, SelectableRow } from '../../components/ui/bulk-actions';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Users, Plus, Pencil, Trash2, Shield, GraduationCap, UserCheck, Loader2, Download, Search, Eye, Mail, Phone, MapPin, BookOpen, Baby, Ban, UserX } from 'lucide-react';

export default function AdminUsersPage({ embedded = false, defaultRole = null }) {
  const { api } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    role: (defaultRole && !['staff', 'parent'].includes(defaultRole)) ? defaultRole : 'all',
    status: 'all',
    search: '',
    created_start: null,
    created_end: null,
  });
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'parent',
    is_active: true
  });

  // Filter definitions
  const filters = [
    {
      key: 'role',
      label: 'Role',
      type: 'select',
      options: [
        { value: 'admin', label: 'Admin' },
        { value: 'instructor', label: 'Instructor' },
        { value: 'volunteer', label: 'Volunteer' },
        { value: 'parent', label: 'Parent' },
        { value: 'partner', label: 'Partner' },
      ],
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ],
    },
    {
      key: 'search',
      label: 'Search',
      type: 'text',
      placeholder: 'Search by name or email...',
    },
    {
      key: 'created',
      label: 'Created Date',
      type: 'dateRange',
    },
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users/?page_size=5000');
      setUsers(response.data?.results || response.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters client-side
  // Staff roles for filtering
  const staffRoles = ['admin', 'super_admin', 'instructor', 'volunteer', 'partner'];

  const filteredUsers = useMemo(() => {
    const staffRolesList = ['admin', 'super_admin', 'instructor', 'volunteer'];
    return users.filter(user => {
      // Page-level role filter (from defaultRole prop)
      if (defaultRole === 'staff' && !staffRolesList.includes(user.role)) return false;
      if (defaultRole === 'parent' && user.role !== 'parent') return false;
      if (defaultRole === 'partner' && user.role !== 'partner') return false;
      if (defaultRole && !['all', 'staff', 'parent', 'partner'].includes(defaultRole) && user.role !== defaultRole) return false;
      
      // Card-level role filter (from clicking stat cards)
      if (activeFilters.role && activeFilters.role !== 'all') {
        if (activeFilters.role === 'admin') {
          if (user.role !== 'admin' && user.role !== 'super_admin') return false;
        } else if (activeFilters.role === 'parent_only') {
          if (user.role !== 'parent' || user.guardian_type === 'guardian') return false;
        } else if (activeFilters.role === 'guardian_only') {
          if (user.role !== 'parent' || user.guardian_type !== 'guardian') return false;
        } else if (user.role !== activeFilters.role) {
          return false;
        }
      }
      
      // Status filter
      if (activeFilters.status && activeFilters.status !== 'all') {
        const isActive = activeFilters.status === 'active';
        if (user.is_active !== isActive) return false;
      }
      
      // Search filter
      if (activeFilters.search) {
        const search = activeFilters.search.toLowerCase();
        const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
        const email = user.email.toLowerCase();
        const phone = (user.phone || '').toLowerCase();
        if (!fullName.includes(search) && !email.includes(search) && !phone.includes(search)) {
          return false;
        }
      }
      
      // Date range filter
      if (activeFilters.created_start) {
        const userDate = new Date(user.created_at);
        if (userDate < activeFilters.created_start) return false;
      }
      if (activeFilters.created_end) {
        const userDate = new Date(user.created_at);
        if (userDate > activeFilters.created_end) return false;
      }
      
      return true;
    });
  }, [users, activeFilters, defaultRole]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await api.patch(`/users/${editingUser.id}/`, formData);
        toast.success('User updated');
      } else {
        await api.post('/users/', { ...formData, password: 'Welcome123!' });
        toast.success('User created with default password: Welcome123!');
      }
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      const data = error.response?.data;
      let message = 'Failed to save user';
      if (data) {
        if (data.detail) message = data.detail;
        else if (typeof data === 'object') {
          const fieldErrors = Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('; ');
          if (fieldErrors) message = fieldErrors;
        }
      }
      toast.error(message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.delete(`/users/${id}/`);
      toast.success('User deleted');
      setSelectedIds(prev => prev.filter(i => i !== id));
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const toggleDisable = async (user) => {
    const newStatus = !user.is_active;
    const action = newStatus ? 'enable' : 'disable';
    if (!window.confirm(`${newStatus ? 'Enable' : 'Disable'} ${user.first_name} ${user.last_name}? ${newStatus ? 'They will regain access.' : 'They will be unable to log in.'}`)) return;
    try {
      await api.patch(`/users/${user.id}/`, { is_active: newStatus });
      toast.success(`${user.first_name} ${user.last_name} ${newStatus ? 'enabled' : 'disabled'}`);
      fetchUsers();
    } catch {
      toast.error(`Failed to ${action} user`);
    }
  };

  const openDetails = async (user) => {
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetailsData(null);
    try {
      // Fetch learners for this parent
      const learnersRes = await api.get(`/learners/?page_size=100`);
      const allLearners = learnersRes.data?.results || learnersRes.data || [];
      const userLearners = allLearners.filter(l => l.parent === user.id);

      // Fetch enrollments
      const enrollmentsRes = await api.get(`/enrollments/?page_size=5000`);
      const allEnrollments = enrollmentsRes.data?.results || enrollmentsRes.data || [];
      const userEnrollments = allEnrollments.filter(e => e.user === user.id);

      setDetailsData({
        ...user,
        learners: userLearners,
        enrollments: userEnrollments,
      });
    } catch (err) {
      setDetailsData({ ...user, learners: [], enrollments: [] });
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleBulkDelete = async (ids) => {
    if (!window.confirm(`Are you sure you want to delete ${ids.length} users?`)) return;
    try {
      let deleted = 0;
      for (const id of ids) {
        try {
          await api.delete(`/users/${id}/`);
          deleted++;
        } catch (err) {
          console.error(`Failed to delete user ${id}:`, err);
        }
      }
      toast.success(`Deleted ${deleted} users`);
      setSelectedIds([]);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete users');
    }
  };

  const handleExport = (ids) => {
    const exportUsers = users.filter(u => ids.includes(u.id));
    const csv = [
      ['Email', 'First Name', 'Last Name', 'Phone', 'Role', 'Status', 'Created At'],
      ...exportUsers.map(u => [
        u.email,
        u.first_name,
        u.last_name,
        u.phone || '',
        u.role,
        u.is_active ? 'Active' : 'Inactive',
        new Date(u.created_at).toLocaleDateString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${exportUsers.length} users`);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      phone: '',
      role: 'parent',
      is_active: true
    });
    setEditingUser(null);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      role: user.role,
      is_active: user.is_active ?? true
    });
    setDialogOpen(true);
  };

  const roleColors = {
    admin: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    super_admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    instructor: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    volunteer: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    parent: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    partner: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  };

  const roleIcons = {
    admin: Shield,
    super_admin: Shield,
    instructor: GraduationCap,
    volunteer: UserCheck,
    parent: Users,
    partner: Users,
  };

  // Stats
  const stats = {
    total: users.length,
    admins: users.filter(u => u.role === 'admin' || u.role === 'super_admin').length,
    instructors: users.filter(u => u.role === 'instructor').length,
    volunteers: users.filter(u => u.role === 'volunteer').length,
    parents: users.filter(u => u.role === 'parent').length,
    partners: users.filter(u => u.role === 'partner').length,
  };

  // Context-aware stat cards based on which page we're on
  const allCards = [
    { key: 'all', label: 'Total', count: stats.total, color: 'text-gray-700' },
    { key: 'admin', label: 'Admins', count: stats.admins, color: 'text-red-600', group: 'staff' },
    { key: 'instructor', label: 'Instructors', count: stats.instructors, color: 'text-blue-600', group: 'staff' },
    { key: 'volunteer', label: 'Volunteers', count: stats.volunteers, color: 'text-purple-600', group: 'staff' },
    { key: 'parent', label: 'Parents', count: stats.parents, color: 'text-green-600', group: 'parent' },
    { key: 'partner', label: 'Partners', count: stats.partners, color: 'text-amber-600', group: 'staff' },
  ];

  const statCards = (() => {
    if (defaultRole === 'staff') {
      const staffUsers = users.filter(u => ['admin','super_admin','instructor','volunteer'].includes(u.role));
      return [
        { key: 'all', label: 'All Staff', count: staffUsers.length, color: 'text-gray-700' },
        { key: 'instructor', label: 'Instructors', count: stats.instructors, color: 'text-blue-600' },
        { key: 'volunteer', label: 'Volunteers', count: stats.volunteers, color: 'text-purple-600' },
        { key: 'admin', label: 'Internal Staff', count: stats.admins, color: 'text-red-600' },
      ];
    }
    if (defaultRole === 'partner') {
      return [
        { key: 'partner', label: 'All Partners', count: stats.partners, color: 'text-amber-600' },
      ];
    }
    if (defaultRole === 'parent') {
      const parentOnly = users.filter(u => u.role === 'parent' && (u.guardian_type === 'parent' || !u.guardian_type));
      const guardianOnly = users.filter(u => u.role === 'parent' && u.guardian_type === 'guardian');
      const allParents = users.filter(u => u.role === 'parent');
      return [
        { key: 'all', label: 'All', count: allParents.length, color: 'text-gray-700' },
        { key: 'parent_only', label: 'Parents', count: parentOnly.length, color: 'text-green-600' },
        { key: 'guardian_only', label: 'Guardians', count: guardianOnly.length, color: 'text-blue-600' },
      ];
    }
    // Single role pages (instructor, volunteer, admin)
    if (defaultRole && defaultRole !== 'all') {
      const card = allCards.find(c => c.key === defaultRole);
      return card ? [card] : allCards;
    }
    return allCards;
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold">Users</h1>
            <p className="text-muted-foreground">Manage all system users</p>
          </div>
        </div>
      )}
      <div className="flex items-center justify-end">
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" onClick={resetForm} data-testid="add-user-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={!!editingUser}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parent">Parent</SelectItem>
                    <SelectItem value="instructor">Instructor</SelectItem>
                    <SelectItem value="volunteer">Volunteer</SelectItem>
                    <SelectItem value="partner">Partner</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">{editingUser ? 'Save Changes' : 'Create User'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by name, email, or phone..."
          value={activeFilters.search}
          onChange={e => setActiveFilters(prev => ({ ...prev, search: e.target.value }))}
          className="pl-10"
        />
      </div>

      {/* Stats — Clickable Filter Cards */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {statCards.map((card) => {
          const isActive = (card.key === 'all' && activeFilters.role === 'all') || activeFilters.role === card.key;
          return (
            <button
              key={card.key}
              onClick={() => setActiveFilters(prev => ({ ...prev, role: card.key === 'all' ? 'all' : card.key }))}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                isActive
                  ? 'border-primary bg-primary/5 shadow-md ring-1 ring-primary/20'
                  : 'border-transparent bg-white hover:border-gray-200 hover:shadow-sm'
              }`}
            >
              <p className={`text-2xl font-bold ${isActive ? 'text-primary' : card.color}`}>{card.count}</p>
              <p className={`text-xs font-medium ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>{card.label}</p>
            </button>
          );
        })}
      </div>

      {/* Bulk Actions & Filters */}
      <BulkActions
        items={filteredUsers}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        onBulkDelete={handleBulkDelete}
        onExport={handleExport}
        filters={filters}
        activeFilters={activeFilters}
        onFilterChange={setActiveFilters}
        entityName="users"
      />

      {/* Users Table */}
      <Card className="card-dashboard">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => {
                  const RoleIcon = roleIcons[user.role] || Users;
                  const isSelected = selectedIds.includes(user.id);
                  
                  return (
                    <TableRow 
                      key={user.id} 
                      className={isSelected ? 'bg-primary/5' : ''}
                      data-testid={`user-row-${user.id}`}
                    >
                      <TableCell>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedIds([...selectedIds, user.id]);
                            } else {
                              setSelectedIds(selectedIds.filter(id => id !== user.id));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {user.first_name} {user.last_name}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.phone || '-'}</TableCell>
                      <TableCell>
                        <Badge className={`${roleColors[user.role]} flex items-center gap-1 w-fit`}>
                          <RoleIcon className="h-3 w-3" />
                          {user.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.is_active ? 'default' : 'secondary'}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {user.role === 'parent' && (
                            <Button variant="ghost" size="icon" onClick={() => openDetails(user)} title="View Details">
                              <Eye className="h-4 w-4 text-blue-500" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => toggleDisable(user)} title={user.is_active ? 'Disable user' : 'Enable user'}>
                            {user.is_active ? <Ban className="h-4 w-4 text-amber-500" /> : <UserCheck className="h-4 w-4 text-green-500" />}
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openEdit(user)} title="Edit">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(user.id)} title="Delete">
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Results count */}
      <p className="text-sm text-muted-foreground text-center">
        Showing {filteredUsers.length} of {users.length} users
      </p>

      {/* Parent Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {detailsData ? `${detailsData.first_name} ${detailsData.last_name}` : 'User Details'}
            </DialogTitle>
          </DialogHeader>
          {detailsLoading ? (
            <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin mx-auto" /></div>
          ) : detailsData && (
            <div className="space-y-5">
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /> {detailsData.email}</div>
                <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /> {detailsData.phone || '—'}</div>
              </div>
              <div className="flex gap-2">
                <Badge className={detailsData.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                  {detailsData.is_active ? 'Active' : 'Inactive'}
                </Badge>
                <Badge variant="outline">{detailsData.role}</Badge>
                {detailsData.guardian_type && detailsData.guardian_type !== 'parent' && (
                  <Badge variant="outline">{detailsData.guardian_type}</Badge>
                )}
                {detailsData.created_at && (
                  <span className="text-xs text-muted-foreground">Joined {new Date(detailsData.created_at).toLocaleDateString()}</span>
                )}
              </div>

              {/* Learners */}
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Baby className="h-4 w-4" /> Learners ({detailsData.learners?.length || 0})
                </h4>
                {detailsData.learners?.length === 0 ? (
                  <p className="text-sm text-muted-foreground bg-gray-50 rounded p-3">No learners associated with this account</p>
                ) : (
                  <div className="space-y-2">
                    {detailsData.learners?.map((l, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{l.first_name} {l.last_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {l.grade && `Grade ${l.grade}`}{l.school ? ` · ${l.school}` : ''}{l.date_of_birth ? ` · DOB: ${l.date_of_birth}` : ''}
                          </p>
                        </div>
                        <div className="flex gap-2 text-xs">
                          {l.allergies && <Badge variant="destructive" className="text-[10px]">Allergies</Badge>}
                          {l.photo_consent && <Badge className="bg-green-100 text-green-700 text-[10px]">Photo ✓</Badge>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Enrollments */}
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <BookOpen className="h-4 w-4" /> Enrollments ({detailsData.enrollments?.length || 0})
                </h4>
                {detailsData.enrollments?.length === 0 ? (
                  <p className="text-sm text-muted-foreground bg-gray-50 rounded p-3">No enrollments found</p>
                ) : (
                  <div className="space-y-2 max-h-[250px] overflow-auto">
                    {detailsData.enrollments?.map((e, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{e.section_title || 'Course'}</p>
                          <p className="text-xs text-muted-foreground">
                            {e.learner_name}{e.created_at ? ` · Enrolled ${new Date(e.created_at).toLocaleDateString()}` : ''}
                          </p>
                        </div>
                        <Badge className={
                          e.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                          e.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                          e.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }>
                          {e.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
